import React, { PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { IconButton, Typography, Tooltip } from '@mic3/platform-ui';

import Icon from 'app/components/atoms/Icon/Icon';
import Resizer from 'app/components/molecules/Resizer/Resizer';
import ThreadsList from 'app/components/organisms/Chat/ThreadsList';
import ThreadView from 'app/components/organisms/Chat/ThreadView';
import { closeThreadPanel, openThreadsList, toggleThreadFollow } from 'store/actions/chat/chatThreadActions';
import { DEFAULT_RIGHT_SIDEBAR_WIDTH, MIN_RIGHT_SIDEBAR_WIDTH } from 'app/config/config';
import { toUniqueUUID } from 'app/utils/string/string-utils';
import { bind } from 'app/utils/decorators/decoratorUtils';
import { get } from 'app/utils/lo/lo';

// Thread panel is the chat's right-hand sidebar — match the global right sidebar's
// configured default/min width (app/config/config) so both sidebars are consistent.
const DEFAULT_PANEL_WIDTH = DEFAULT_RIGHT_SIDEBAR_WIDTH;
const MIN_PANEL_WIDTH = MIN_RIGHT_SIDEBAR_WIDTH;
const MIN_CHAT_WIDTH = 360;

// Remember the user's chosen width across panel open/close within the session.
let cachedPanelWidth = DEFAULT_PANEL_WIDTH;

const PanelWrapper = styled.div`
    position: relative;
    width: ${({ width }) => width}px;
    min-width: ${MIN_PANEL_WIDTH}px;
    height: 100%;
    display: flex;
    flex-direction: column;
    /* margin-left keeps the resizer strip clear of the chat column; padding-left keeps
       the panel content clear of the strip — matches the Messages-panel resizer spacing
       (12px content→strip, 8px strip, 24px strip→content) */
    /* Cancel ALive__ChatContainer's 24px page-edge right padding so the panel sits flush
       with the viewport right edge — making its box (width = DEFAULT_RIGHT_SIDEBAR_WIDTH)
       overlap exactly with the global right sidebar (About/Members/…). Without this the
       panel ends 24px short and the right edge jumps when switching to a global sidebar. */
    /* 24px horizontal padding aligns thread content with the global right sidebar's content
       inset (~24px each side: 8px strip / scrollbar gutter + 16px Container padding). Content
       (header close/search, message rows, list cards) lands at the sidebar's content x on both
       edges — left ≈ 1000, right ≈ 1576 — so nothing touches the panel edge (Figma) and the
       items stay right-aligned with each other. margin-right:-24 cancels the page inset (box
       flush at the viewport edge); padding-right then re-insets the content like the sidebar. */
    margin-left: 24px;
    margin-right: -24px;
    padding: 0 16px 0 24px;
    background: ${({ theme }) => theme.material.colors.background.default};
    /* A stray empty MUI list renders as a direct child here (same as AliveChat's
       ChatContainer) — hide it so it doesn't steal 16px at the bottom and push the
       thread composer up out of alignment with the main chat composer. */
    & > ul.MuiList-root {
        display: none;
    }
    ${({ resizing }) => (resizing ? 'user-select: none;' : '')}
    @media (max-width: 961px) {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 5;
        margin-left: 0;
        padding: 0 8px;
    }
`;

// Height matches ChatHeader's FlexWrapper (content row 49px incl. divider) so the
// panel divider lines up with the chat header divider (Figma).
const PanelHeader = styled.div`
    box-sizing: border-box;
    height: 49px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border-bottom: 1px solid ${({ theme }) => theme.material.colors.background.divider};
    & .MuiIconButton-root {
        padding: 6px;
    }
    /* cancel the back button's inner padding so its glyph aligns with the divider /
       content left edge (Figma) */
    & .MuiIconButton-root:first-child {
        margin-left: -6px;
    }
`;

const TitleBlock = styled.div`
    flex: 1;
    min-width: 0;
    & .MuiTypography-subtitle2 {
        font-size: 16px;
        line-height: 1.2;
        /* thread-detail title is the parent message text — ellipsize on overflow */
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    & .MuiTypography-caption {
        display: block;
        line-height: 1.2;
    }
`;

// Figma: icon + title on the first line, subtitle below aligned with the icon.
const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    & .Icon:before {
        font-size: 18px;
    }
`;

class ThreadPanel extends PureComponent {
    static propTypes = {
        rid: PropTypes.string,
        rel: PropTypes.object,
        readOnly: PropTypes.bool,
        isOpen: PropTypes.bool,
        mode: PropTypes.string,
        parentMessage: PropTypes.object,
        currentUserId: PropTypes.string,
        openThreadsList: PropTypes.func.isRequired,
        closeThreadPanel: PropTypes.func.isRequired,
        toggleThreadFollow: PropTypes.func.isRequired,
    };

    state = { width: cachedPanelWidth, resizing: false };

    panelRef = createRef();

    // Resizer computes the new width from `resizeElem.current.offsetLeft`, which assumes a
    // viewport-based offset (top-level sidebars). The panel is nested in ChatFlexRow, so we
    // hand Resizer an adapter whose offsetLeft is the panel's viewport-left.
    resizeElemAdapter = {
        current: {
            get offsetLeft() {
                const node = document.querySelector('[data-thread-panel]');
                return node ? node.getBoundingClientRect().left : 0;
            },
        },
    };

    @bind
    onBack() {
        const { mode, openThreadsList, closeThreadPanel, rid } = this.props;
        if (mode === 'thread') openThreadsList(rid);
        else closeThreadPanel();
    }

    @bind
    onToggleFollow() {
        const { parentMessage, currentUserId, toggleThreadFollow } = this.props;
        if (!parentMessage) return;
        const following = (parentMessage.replies || []).includes(currentUserId);
        toggleThreadFollow(parentMessage.id, !following);
    }

    @bind
    setResizing(resizing) {
        this.setState({ resizing });
    }

    @bind
    onResize(width) {
        const node = this.panelRef.current;
        const rowWidth = node && node.parentElement ? node.parentElement.getBoundingClientRect().width : window.innerWidth;
        const maxWidth = Math.max(MIN_PANEL_WIDTH, rowWidth - MIN_CHAT_WIDTH);
        const clamped = Math.min(Math.max(width, MIN_PANEL_WIDTH), maxWidth);
        cachedPanelWidth = clamped;
        this.setState({ width: clamped });
    }

    render() {
        const { isOpen, mode, rid, rel, readOnly, parentMessage, currentUserId, closeThreadPanel } = this.props;
        const { width, resizing } = this.state;
        if (!isOpen) return null;
        // thread-detail title = parent text, else the attachment name (attachment-only message),
        // else 'Thread'. A removed parent already carries "Message removed" as its text.
        const title = mode === 'thread' ? (parentMessage?.text || parentMessage?.file?.name || 'Thread') : 'Threads';
        const following = mode === 'thread' && !!parentMessage && (parentMessage.replies || []).includes(currentUserId);
        const isNarrow = typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 961px)').matches;
        return (
            <PanelWrapper width={width} resizing={resizing} ref={this.panelRef} data-thread-panel>
                {!isNarrow && (
                    <Resizer
                        size={width}
                        isResizing={this.setResizing}
                        onResize={this.onResize}
                        resizeElem={this.resizeElemAdapter}
                        defaultSidebarWidth={DEFAULT_PANEL_WIDTH}
                    />
                )}
                <PanelHeader>
                    <IconButton onClick={this.onBack} aria-label="back">
                        <Icon name="arrow-left" size="md" />
                    </IconButton>
                    <TitleBlock>
                        <TitleRow>
                            <Icon name="thread" type="af" size="sm" />
                            <Typography variant="subtitle2" title={title}>{title}</Typography>
                        </TitleRow>
                        {rel && rel.id && (
                            <Typography variant="caption" noWrap>
                                {`${rel.name || ''} #${toUniqueUUID(rel.id)}`}
                            </Typography>
                        )}
                    </TitleBlock>
                    {mode === 'thread' && parentMessage && (
                        <Tooltip title={following ? 'Following' : 'Not Following'}>
                            <IconButton onClick={this.onToggleFollow} aria-label={following ? 'Following' : 'Not Following'}>
                                <Icon name={following ? 'bell-outline' : 'bell-off-outline'} size="md" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Close">
                        <IconButton onClick={closeThreadPanel} aria-label="close">
                            <Icon name="close" size="md" />
                        </IconButton>
                    </Tooltip>
                </PanelHeader>
                {mode === 'thread' ? <ThreadView rid={rid} readOnly={readOnly} /> : <ThreadsList rid={rid} />}
            </PanelWrapper>
        );
    }
}

export default connect(
    state => ({
        isOpen: state.chat.thread.isOpen,
        mode: state.chat.thread.mode,
        parentMessage: state.chat.thread.parentMessage,
        currentUserId: get(state, 'user.chat.id'),
    }),
    { closeThreadPanel, openThreadsList, toggleThreadFollow }
)(ThreadPanel);
